import './NumberField.scss'
import type { ChangeEvent, ComponentProps } from 'react'
import { clx } from '../clx'

export interface NumberFieldProps extends Omit<
  ComponentProps<'input'>,
  'type' | 'size' | 'value' | 'defaultValue' | 'onChange' | 'inputMode' | 'min' | 'max' | 'step'
> {
  /** `''` is the empty field. */
  value?: number | ''
  defaultValue?: number | ''
  /** Gives a number, or `''` when the field is empty or not a number yet. Never NaN. */
  onChange?: (value: number | '', event: ChangeEvent<HTMLInputElement>) => void
  min?: number
  max?: number
  step?: number | 'any'
  /** Mobile keyboard. Defaults to `decimal` for a fractional or `any` step, else `numeric`. */
  inputMode?: 'numeric' | 'decimal'
  size?: 'sm' | 'md'
}

/** Parse a number input; anything that is not a finite number is the empty value. */
function toNumberValue(input: HTMLInputElement): number | '' {
  if (input.value === '') return ''
  const n = input.valueAsNumber
  return Number.isFinite(n) ? n : ''
}

/** Numeric input with native min/max/step. Spread Field's control props onto it. */
export function NumberField({
  value,
  defaultValue,
  onChange,
  step,
  inputMode,
  size = 'md',
  className,
  ref,
  ...rest
}: NumberFieldProps) {
  const isDecimal = step === 'any' || (step !== undefined && !Number.isInteger(step))
  return (
    <input
      {...rest}
      ref={ref}
      type="number"
      inputMode={inputMode ?? (isDecimal ? 'decimal' : 'numeric')}
      {...(step !== undefined && { step })}
      {...(value !== undefined ? { value } : defaultValue !== undefined && { defaultValue })}
      className={clx('number-field', size !== 'md' && `number-field--is-${size}`, className)}
      onChange={(event) => onChange?.(toNumberValue(event.currentTarget), event)}
    />
  )
}
